/* eslint-disable react-hooks/rules-of-hooks -- создаем хуки в контексте для оптимизации ререндеров */
import {
    createContext,
    type FunctionComponent,
    type MutableRefObject,
    type PropsWithChildren,
    type RefObject,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import isEqual from 'lodash/isEqual';
import { noop, useIsomorphicLayoutEffect, useResizeObserver } from '@direct-frontend/stdlib';

type ContainerTreeSizeState = {
    isInitialed: boolean;
    width: number;
    left: number;
    scrollLeft: number;
};

export type SubscribeHandler = (options: ContainerTreeSizeState) => void;

type ContainerTreeSizeSubscribeState = {
    useSubscribe: (cb: SubscribeHandler) => void;
};

const ContainerTreeSizeSubscribeContext = createContext<ContainerTreeSizeSubscribeState>({
    useSubscribe: noop,
});

export interface ContainerTreeSizeProviderProps {
    /**
     * Реф на контейнер
     */
    containerRef: RefObject<HTMLElement>;
}

/*
 * Провайдер подписки на размер контейнера
 */
export const ContainerTreeSizeProvider: FunctionComponent<PropsWithChildren<ContainerTreeSizeProviderProps>> = ({
    containerRef,
    children,
}) => {
    /**
     * Мутабельный стейт провайдера
     */
    const [state] = useState<{
        state: Readonly<ContainerTreeSizeState>;
        subscribers: Set<MutableRefObject<SubscribeHandler>>;
    }>(() => {
        return {
            state: Object.freeze({
                isInitialed: false,
                width: 0,
                left: 0,
                scrollLeft: 0,
            }),
            subscribers: new Set<MutableRefObject<SubscribeHandler>>(),
        };
    });

    /**
     * Хук для подписки на изменение размера контейнера
     */
    const [useSubscribe] = useState<(cb: SubscribeHandler) => void>(() => (cb: SubscribeHandler) => {
        // Мемоизируем колбек чтобы не зависеть от его изменений
        const refCb = useRef(cb);

        refCb.current = cb;

        // Счетчик ререндеров для useIsomorphicLayoutEffect
        const refCount = useRef(0);

        // Запускаем колбек
        useIsomorphicLayoutEffect(() => {
            // Запускаем колбек только если контейнер инициализирован и не было ререндеров
            if (state.state.isInitialed && refCount.current < 1) {
                refCount.current += 1;
                cb(state.state);
            }
        }, [cb]);

        useEffect(() => {
            // Сбрасываем счетчик ререндеров
            refCount.current = 0;
        });

        useEffect(() => {
            // подписка
            state.subscribers.add(refCb);

            return () => {
                // отписка
                state.subscribers.delete(refCb);
            };
        }, []);
    });
    // Дополнительно мемоизируем реф контейнера
    const ref = useRef(containerRef);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-312943
    ref.current = containerRef;

    // Хэндлеры для вызова подписчиков при изменении размера и скролле контейнера
    const [{ handleResize, handleScroll }] = useState(() => {
        // Функция для вызова подписчиков
        function runSubscribers() {
            state.subscribers.forEach(({ current }) => {
                current(state.state);
            });
        }

        return {
            handleResize: () => {
                // Проверяем изменение размера контейнера
                let isChanged = false;
                const element = ref.current.current;

                if (element instanceof HTMLElement) {
                    const contentRect = element.getBoundingClientRect();
                    // Создаем новый стейт
                    const newState = Object.freeze({
                        isInitialed: true,
                        width: contentRect.width,
                        left: contentRect.left,
                        scrollLeft: element.scrollLeft,
                    });

                    // Проверяем изменение стейта
                    isChanged = !isEqual(state.state, newState);
                    state.state = newState;
                }

                // Вызываем подписчиков если стейт изменился
                if (isChanged) {
                    runSubscribers();
                }
            },
            handleScroll: (event: Event) => {
                const element = event.target;

                if (element instanceof HTMLElement) {
                    const newScrollLeft = element.scrollLeft;

                    // Проверяем изменение скролла контейнера
                    if (newScrollLeft !== state.state.scrollLeft) {
                        state.state = Object.freeze({
                            ...state.state,
                            scrollLeft: newScrollLeft,
                        });

                        // Вызываем подписчиков если стейт проинициализирован
                        if (state.state.isInitialed) {
                            runSubscribers();
                        }
                    }
                }
            },
        };
    });

    const observer = useResizeObserver(handleResize);

    useEffect(() => {
        if (observer && containerRef.current) {
            observer.observe(containerRef.current);
        }
    }, [containerRef, observer]);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        container.addEventListener('scroll', handleScroll);

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [containerRef, handleScroll]);

    return (
        <ContainerTreeSizeSubscribeContext.Provider value={{ useSubscribe }}>
            {children}
        </ContainerTreeSizeSubscribeContext.Provider>
    );
};

/*
 * Хук для подписки на изменение размера контейнера
 */
export function useContainerTreeSizeSubscribe(cb: SubscribeHandler) {
    const { useSubscribe } = useContext(ContainerTreeSizeSubscribeContext);

    useSubscribe(cb);
}
